/** Variables passed to a GraphQL operation. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type GraphqlVariables = {};

/** Key selecting which Amplify client to use. */
export type AmplifyClientKey = "appsyncBff" | "springBff";

/** Standard async-operation result — framework-agnostic. */
export interface GraphqlResult<TData> {
  data: TData | undefined;
  loading: boolean;
  error: Error | undefined;
}

/** Resolved value returned by trigger / mutate functions. */
export interface TriggerResult<TData> {
  data: TData | undefined;
}

export type LazyQueryTrigger<TData, TVariables> = (options?: {
  variables?: TVariables;
  context?: Record<string, unknown>;
}) => Promise<TriggerResult<TData>>;

export type MutationTrigger<TData, TVariables> = (options?: {
  variables?: TVariables;
}) => Promise<TriggerResult<TData>>;

/**
 * GraphQL error shape — works for both Apollo and Amplify errors.
 * Checks for `.graphQLErrors` and `.networkError` via duck-typing
 * so we don't depend on the ApolloError class.
 */
export interface GraphqlErrorLike {
  message: string;
  graphQLErrors?: ReadonlyArray<{
    message: string;
    extensions?: Record<string, unknown>;
  }>;
  networkError?: {message: string; statusCode?: number} | null;
}

/** Type guard: does this error carry GraphQL/network error details?
 * NOTE: Duck-typing without instanceof Error — Amplify throws raw { data, errors }
 * objects that aren't Error instances but may carry graphQLErrors/networkError. */
export const isGraphqlErrorLike = (error: unknown): error is GraphqlErrorLike =>
  typeof error === "object" &&
  error !== null &&
  "message" in error &&
  ("graphQLErrors" in error || "networkError" in error);

/**
 * Coerce an unknown thrown value into an Error.
 * Amplify's client.graphql() throws the raw { data, errors } response object
 * on GraphQL errors rather than an Error instance.
 */
export function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (
    typeof err === "object" &&
    err !== null &&
    "errors" in err &&
    Array.isArray((err as {errors: unknown[]}).errors)
  ) {
    const errors = (err as {errors: Array<{message?: string}>}).errors;
    const message = errors.map(e => e.message ?? "Unknown error").join("; ");
    return new Error(message);
  }
  return new Error(String(err));
}
